import { cn } from "~/lib/utils";
import Spinner from "./Spinner";

interface Props {
  type?: "submit" | "reset" | "button" | undefined;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /**
   * Classes for the button
   *
   * If you want separate classes for a disabled state, you have to do that yourself. Otherwise tailwind would ignore it
   * @example `className={disabled ? "bg-lightGrey" : "bg-black"}`
   *
   */
  className?: string;
  text: string;
  subText?: string;
  loadingText?: string;
  textTheme?: "white" | "black" | "salmon";
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  /**
   * Class name applied to the button's text element
   *
   * If you want separate classes for a disabled state, you have to do that yourself. Otherwise tailwind would ignore it
   * @example `textClassName={disabled ? "bg-lightGrey" : "bg-black"}`
   */
  textClassName?: string;
  disabled?: boolean;
  loading?: boolean;
  theme?: "salmon" | "black" | "white" | "lightGrey";
  fontBold?: boolean;
  dataTestId?: string;
}

export default function Button({
  type,
  onClick,
  className,
  text,
  subText,
  loadingText = "Loading...",
  textTheme = "white",
  leftIcon,
  rightIcon,
  textClassName,
  disabled: _disabled = false,
  loading = false,
  theme = "black",
  fontBold = true,
  dataTestId,
}: Props) {
  // Disabled should only have an effect if loading is false
  const disabled = loading ? false : _disabled;

  const textClass = {
    "text-white": textTheme === "white" && !disabled,
    "text-black": textTheme === "black" && !disabled,
    "text-salmon": textTheme === "salmon" && !disabled,
    "text-darkGrey": disabled,
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={_disabled || loading}
      className={cn(
        "rounded-full px-4 py-3",
        {
          "bg-neutralGreyDark": theme === "black" && !disabled,
          "bg-white": theme === "white" && !disabled,
          "bg-salmon": theme === "salmon" && !disabled,
          "bg-lightGrey": disabled || theme === "lightGrey",
        },
        className,
      )}
      data-testid={dataTestId}
    >
      <span
        className={cn(
          "flex items-center justify-center",
          {
            "font-bold": fontBold,
          },
          textClass,
          textClassName,
        )}
      >
        {leftIcon ? <span className="mr-2">{leftIcon}</span> : null}
        {loading ? (
          <div className="flex items-center justify-center gap-2 min-h-6">
            <Spinner size="small" className={textClass} />
            {loadingText}
          </div>
        ) : subText ? (
          <span className="flex flex-col items-center justify-center">
            <span>{text}</span>
            <span className="font-normal text-[11px] leading-[1.4] opacity-[.78]">
              {subText}
            </span>
          </span>
        ) : (
          text
        )}

        {rightIcon ? <span className="ml-2">{rightIcon}</span> : null}
      </span>
    </button>
  );
}
